import React, { Component } from 'react';
import { array, func, string } from 'prop-types';
import classnames from 'classnames';

class Autocomplete extends Component {
  static propTypes = {
    items: array.isRequired,
    getItemValue: func.isRequired,
    onSelectItem: func.isRequired,
    defaultValue: string,
    placeholder: string,
  };

  static defaultProps = {
    defaultValue: '',
    placeholder: null,
  };

  state = {
    value: this.props.defaultValue,
    itemsMatched: [],
    forceHideResults: false,
    itemPreSelected: null,
  };

  constructor(props) {
    super(props);

    this.indexItemSelected = -1;

    this.match = this.match.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.hideResults = this.hideResults.bind(this);
    this.resetState = this.resetState.bind(this);
    this.renderItem = this.renderItem.bind(this);
  }

  /**
   * {@inheritdoc}
   */
  componentDidMount() {
    document.addEventListener('click', this.hideResults);
  }

  /**
   * {@inheritdoc}
   */
  componentDidUpdate(prevProps) {
    const { defaultValue } = this.props;

    if (prevProps.defaultValue !== defaultValue) {
      this.setState({ value: defaultValue });
    }
  }

  /**
   * {@inheritdoc}
   */
  componentWillUnmount() {
    document.removeEventListener('click', this.hideResults);
  }

  /**
   * @param {Object} item
   * @param {String} value
   *
   * @return {Boolean}
   */
  match(item, value) {
    return this.props.getItemValue(item).toLowerCase().includes(value.toLowerCase());
  }

  /**
   * @param {Event} event
   */
  onChange(event) {
    const { value } = event.target;
    const currentValue = { uuid: null, name: value };
    const itemsMatched = value.length ? this.props.items.filter(item => this.match(item, value)) : [];

    // Add current value on top of research
    if (value.length) {
      const matchItem = this.props.items.find(item => item.name.toLowerCase() === value.toLowerCase());

      if (!matchItem) {
        itemsMatched.unshift(currentValue);
      }
    }

    this.setState({
      value,
      itemsMatched,
    });
  }

  /**
   * @param {Event} event
   */
  onKeyDown(event) {
    const { itemsMatched, itemPreSelected } = this.state;
    const maxItems = itemsMatched.length - 1;

    if (!itemPreSelected) {
      this.setState({ itemPreSelected: 0 });
    }

    this.setState({ forceHideResults: false });

    // Press enter
    if (event.keyCode === 13) {
      const selectedItem = itemsMatched[this.indexItemSelected];

      if (!selectedItem) {
        return;
      }

      this.setState({ value: selectedItem.name });
      this.onSelect(selectedItem);
    } else if (event.keyCode === 40) {
      this.indexItemSelected = Math.min(this.indexItemSelected + 1, maxItems);
      const selectedItem = itemsMatched[this.indexItemSelected];

      this.setState({
        itemPreSelected: this.indexItemSelected,
        value: selectedItem.name,
      });
    } else if (event.keyCode === 38) {
      this.indexItemSelected = Math.max(this.indexItemSelected - 1, 0);
      const selectedItem = itemsMatched[this.indexItemSelected];

      this.setState({
        itemPreSelected: this.indexItemSelected,
        value: selectedItem.name,
      });
    }
  }

  /**
   * @param {MouseEvent} event
   * @param {Boolean}    force
   */
  hideResults(event, force = false) {
    if ((!event && !force) || (event && event.path.includes(this.autocomplete))) {
      return;
    }

    this.setState({ forceHideResults: true });
  }

  resetState() {
    this.indexItemSelected = 0;

    this.setState({ forceHideResults: false });
  }

  /**
   * @param {Object} item
   */
  onSelect(item) {
    const { getItemValue, onSelectItem } = this.props;

    this.setState({ value: getItemValue(item) });

    this.hideResults(null, true);
    onSelectItem(item);
  }

  /**
   * @param {Number} index
   */
  onHoverItem(index) {
    this.indexItemSelected = index;
    this.setState({ itemPreSelected: index });
  }

  /**
   * @param {Object} item
   * @param {Number} index
   */
  renderItem(item, index) {
    const value = this.props.getItemValue(item);
    const itemClasses = classnames('item', {
      'item--selected': this.indexItemSelected === index,
    });

    return (
      <div
        key={`${value}-${index}`}
        className={itemClasses}
        onClick={() => this.onSelect(item)}
        onMouseOver={() => this.onHoverItem(index)}
      >
        <span>{value}</span>
      </div>
    );
  }

  /**
   * {@inheritdoc}
   */
  render() {
    const { placeholder } = this.props;
    const { value, itemsMatched, forceHideResults } = this.state;
    const resultsClasses = classnames('autocomplete__results', {
      'autocomplete__results--open': forceHideResults === false && itemsMatched.length,
    });

    return (
      <div
        ref={(autocomplete) => { this.autocomplete = autocomplete; }}
        className="autocomplete"
        onFocus={this.resetState}
      >
        <input
          className="autocomplete__input"
          value={value}
          onChange={this.onChange}
          onKeyDown={this.onKeyDown}
          placeholder={placeholder}
          onFocus={() => this.onChange({ target: { value } })}
        />
        <div className={resultsClasses}>
          {itemsMatched.map(this.renderItem)}
        </div>
      </div>
    );
  }
}

export default Autocomplete;



// WEBPACK FOOTER //
// ./src/modules/common/components/Autocomplete.js